/**
 * AdminNewsEditor — list + create/edit screen used by admin.
 * Lives inside Settings → "News".
 *
 * Responsibilities:
 *   - List all news (drafts + published) sorted by created_at desc
 *   - Create/edit with Tiptap rich editor
 *   - Toggle published flag
 *   - Show created_at + view_count for each item
 *   - Delete with confirm
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Plus, Edit2, Trash2, Save, X, Eye, EyeOff, Loader2, Newspaper, AlertTriangle, ArrowLeft, Globe, Calendar,
} from 'lucide-react';
import RichTextEditor from './RichTextEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminNewsEditor({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // mode: 'list' | 'edit'
  const [mode, setMode] = useState('list');
  const [editing, setEditing] = useState(null); // { id?, title, body_html, published }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmDel, setConfirmDel] = useState(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API_URL}/api/news/`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setItems(await r.json());
    } catch (e) { setError(e.message || String(e)); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const startCreate = () => { setEditing({ title: '', body_html: '', published: true }); setMode('edit'); };
  const startEdit = (item) => { setEditing({ ...item }); setMode('edit'); };

  const save = async () => {
    if (!editing.title.trim()) { setError('Title is required'); return; }
    if (!editing.body_html || editing.body_html === '<p></p>') { setError('Body cannot be empty'); return; }
    setBusy(true); setError('');
    try {
      const url = editing.id ? `${API_URL}/api/news/${editing.id}` : `${API_URL}/api/news/`;
      const method = editing.id ? 'PUT' : 'POST';
      const r = await fetch(url, {
        method, headers,
        body: JSON.stringify({
          title: editing.title.trim(),
          body_html: editing.body_html,
          published: !!editing.published,
        }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) { setError(body.detail || `HTTP ${r.status}`); return; }
      setMode('list'); setEditing(null);
      load();
    } catch (e) { setError(e.message || 'Network error'); }
    finally { setBusy(false); }
  };

  const togglePublish = async (item) => {
    try {
      const r = await fetch(`${API_URL}/api/news/${item.id}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ published: !item.published }),
      });
      if (!r.ok) return;
      load();
    } catch (_) { /* silent */ }
  };

  const performDelete = async () => {
    if (!confirmDel) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/news/${confirmDel.id}`, { method: 'DELETE', headers });
      if (!r.ok) {
        const b = await r.json().catch(() => ({}));
        setError(b.detail || `HTTP ${r.status}`);
        return;
      }
      setConfirmDel(null);
      load();
    } catch (e) { setError(e.message || 'Network error'); }
    finally { setBusy(false); }
  };

  const fmt = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  if (mode === 'edit' && editing) {
    return (
      <div className="space-y-4" data-testid="admin-news-edit">
        <div className="flex items-center justify-between">
          <button onClick={() => { setMode('list'); setEditing(null); setError(''); }}
            className="text-sm text-slate-600 hover:text-slate-800 flex items-center gap-1.5"
            data-testid="news-edit-back">
            <ArrowLeft className="w-4 h-4" /> Back to list
          </button>
          <h3 className="text-base font-bold text-slate-800">{editing.id ? 'Edit news' : 'New post'}</h3>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">Title</label>
            <input
              type="text"
              value={editing.title}
              onChange={(e) => setEditing({ ...editing, title: e.target.value })}
              maxLength={200}
              data-testid="news-edit-title"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-cyan-500"
              placeholder="A short, attention-grabbing title"
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">Body</label>
            <RichTextEditor
              value={editing.body_html}
              onChange={(html) => setEditing({ ...editing, body_html: html })}
              placeholder="Write the announcement…"
              token={token}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!editing.published}
              onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
              data-testid="news-edit-published"
              className="accent-cyan-600 w-4 h-4"
            />
            Published — visible to all customers
          </label>
          {error && <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="news-edit-error">{error}</p>}
          <div className="flex gap-2 pt-2 border-t border-slate-100">
            <button onClick={save} disabled={busy} data-testid="news-edit-save"
              className="px-5 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-bold hover:shadow-md disabled:opacity-50 inline-flex items-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {editing.id ? 'Save changes' : 'Publish post'}
            </button>
            <button onClick={() => { setMode('list'); setEditing(null); }} disabled={busy}
              className="px-5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-medium">
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-news-list">
      <div className="flex items-start gap-3 p-4 bg-cyan-50 border border-cyan-200 rounded-xl">
        <Newspaper className="w-5 h-5 text-cyan-700 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-cyan-900 flex-1">
          <p className="font-semibold mb-1">News & announcements</p>
          <p>Posts are visible to every authenticated client in their News bell. Drafts (Published off) are hidden.</p>
        </div>
        <button onClick={startCreate} data-testid="news-create-btn"
          className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-bold hover:shadow-md inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> New post
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500"><Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-400">
          <Newspaper className="w-10 h-10 mx-auto mb-2 opacity-40" />
          No news posts yet. Click "New post" to create the first one.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">Title</th>
                <th className="px-4 py-2 text-left">Created</th>
                <th className="px-4 py-2 text-center">Status</th>
                <th className="px-4 py-2 text-center">Views</th>
                <th className="px-4 py-2 text-right w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((n) => (
                <tr key={n.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`news-row-${n.id}`}>
                  <td className="px-4 py-3 font-semibold text-slate-800">{n.title}</td>
                  <td className="px-4 py-3 text-xs text-slate-500 font-mono whitespace-nowrap"><Calendar className="w-3.5 h-3.5 inline mr-1 opacity-60" />{fmt(n.created_at)}</td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => togglePublish(n)} data-testid={`news-toggle-${n.id}`}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${n.published ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}>
                      {n.published ? <Globe className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      {n.published ? 'Published' : 'Draft'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-center text-slate-600 font-mono" data-testid={`news-views-${n.id}`}>
                    <Eye className="w-3.5 h-3.5 inline mr-1 opacity-60" />{n.view_count}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => startEdit(n)} title="Edit" data-testid={`news-edit-${n.id}`}
                      className="p-1.5 text-cyan-600 hover:bg-cyan-50 rounded mr-1"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => setConfirmDel(n)} title="Delete" data-testid={`news-delete-${n.id}`}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-slate-100">
            {items.map((n) => (
              <div key={`m-${n.id}`} className="p-3" data-testid={`news-card-${n.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-800 text-sm truncate">{n.title}</div>
                    <div className="text-[11px] text-slate-500 font-mono">{fmt(n.created_at)}</div>
                  </div>
                  <button onClick={() => togglePublish(n)}
                    className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${n.published ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>
                    {n.published ? <Globe className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                    {n.published ? 'Published' : 'Draft'}
                  </button>
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-xs text-slate-500"><Eye className="w-3 h-3 inline mr-1" />{n.view_count} views</span>
                  <div className="flex gap-1">
                    <button onClick={() => startEdit(n)} className="p-1.5 text-cyan-600 hover:bg-cyan-50 rounded"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => setConfirmDel(n)} className="p-1.5 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" data-testid="news-delete-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full overflow-hidden">
            <div className="bg-red-600 text-white px-5 py-3 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              <h3 className="font-bold text-sm">Delete this post?</h3>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-slate-700">"<strong>{confirmDel.title}</strong>" will be permanently removed for everyone.</p>
              <div className="flex gap-2">
                <button onClick={() => setConfirmDel(null)} className="flex-1 px-3 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium">Cancel</button>
                <button onClick={performDelete} disabled={busy} data-testid="news-delete-confirm"
                  className="flex-1 px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-bold disabled:opacity-50 inline-flex items-center justify-center gap-1">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
